import { driverLicenseExampleValues, zDriverLicense } from '@/general/src/insuranceDoc/driverLicense/shared.js'
import { eptsExampleValues, zEpts } from '@/general/src/insuranceDoc/epts/shared.js'
import { passportExampleValues, zPassportWithUploads } from '@/general/src/insuranceDoc/passport/shared.js'
import { ptsExampleValues, zPts } from '@/general/src/insuranceDoc/pts/shared.js'
import { stsExampleValues, zSts } from '@/general/src/insuranceDoc/sts/shared.js'
import {
  filterPersonalKaskoByOsagoInsuranceScenarioKeys,
  filterPersonalKaskoFromScratchInsuranceScenarioKeys,
  zPersonalKaskoByOsagoInsuranceScenarioKey,
  zPersonalKaskoFromScratchInsuranceScenarioKey,
  zPersonalKaskoInsuranceScenarioKey,
} from '@/general/src/insuranceScenario/shared.js'
import type { InsuranceTypeGeneral } from '@/general/src/insuranceType/shared.js'
import { zDateString, zDateStringOrEmpty } from '@/general/src/other/date.js'
import { zPhoneRequired } from '@/general/src/other/phone.js'
import { zEmailRequired, zStringOptionalNullable, zStringRequired } from '@/general/src/other/validation.js'
import type { InsuranceScenarioKey } from '@prisma/client'
import { z } from 'zod'

// "Выбор из:
// Личные
// Учебная езда
// Инкасация
// Скорая помощь
// Такси
// Дорожные и спецаильные ТС
// Прочие
// Регулярные/по заказам пассажирские перевозки
// Перевозка опасных и легковоспламеняемых грузров
// Экстренные и коммунальные службы
// Прокат/краткосрочная аренда"
export const zVehicleUsagePurpose = z.enum([
  'personal',
  'training',
  'incasation',
  'emergency',
  'taxi',
  'special',
  'other',
  'regular',
  'dangerous',
  'extraneous',
  'rental',
])
export const vehicleUsagePurposeKeysMap: Record<z.infer<typeof zVehicleUsagePurpose>, string> = {
  personal: 'Личные',
  training: 'Учебная езда',
  incasation: 'Инкасация',
  emergency: 'Скорая помощь',
  taxi: 'Такси',
  special: 'Дорожные и спецаильные ТС',
  other: 'Прочие',
  regular: 'Регулярные/по заказам пассажирские перевозки',
  dangerous: 'Перевозка опасных и легковоспламеняемых грузров',
  extraneous: 'Экстренные и коммунальные службы',
  rental: 'Прокат/краткосрочная аренда',
}

export const zPersonalKaskoGeneralFields = z.object({
  // "Дата начала самого полиса
  insuranceStartDate: zDateString,

  // "Цель использования" -
  vehicleUsagePurpose: zVehicleUsagePurpose.optional().nullable(),

  // “Дата начала пользования/приобретения ТС” - надо вписать дату
  lastOwnershipStartDate: zDateStringOrEmpty, // ingos, alfa, vsk

  // “Пробег (км)” - надо вписать число
  mileage: zStringOptionalNullable, // rosgos, ingos

  // “Стоимость автомобиля” - надо вписать число
  vehicleCost: zStringOptionalNullable, // rosgos, ingos, vsk

  // “Расположение руля”
  steeringWheelLocation: z
    .union([z.enum(['Левый', 'Правый']), z.literal('')])
    .nullable()
    .optional(), // ingos

  // “Коробка передач” - выбор: Механическая/Автоматическая
  transmissionType: z
    .union([z.enum(['Механическая', 'Автоматическая']), z.literal('')])
    .nullable()
    .optional(), // renes, ingos

  // “Цвет кузова” - или выбор или вписываем, а потом ИИ выбирает нужный из огромного списка
  vehicleColor: zStringOptionalNullable, // ingos

  // “Наличие детей” - выбор: Дети есть/Детей нет
  children: z
    .union([z.enum(['Дети есть', 'Детей нет']), z.literal('')])
    .nullable()
    .optional(), // renes

  // “Семейное положение” - выбор: Холост/Женат
  maritalStatus: z
    .union([z.enum(['Холост', 'Женат']), z.literal('')])
    .nullable()
    .optional(), // renes, vsk
})
export type PersonalKaskoGeneralFields = z.infer<typeof zPersonalKaskoGeneralFields>
export type PersonalKaskoGeneralFieldsName = keyof PersonalKaskoGeneralFields

export const zPersonalKaskoFromScratchInput = z
  .object({
    policyType: z.literal('fromScratch'),
    insuranceScenariosKeys: z.array(zPersonalKaskoFromScratchInsuranceScenarioKey),
    stsOrPtsOrEpts: z.enum(['sts', 'pts', 'epts']),
    policyholderPhone: zPhoneRequired,
    policyholderEmail: zEmailRequired,
    policyholderIsCarOwner: z.boolean(),
    policyholderPassport: zPassportWithUploads,
    carOwnerPassport: zPassportWithUploads.nullable().optional(),
    driversLicenses: z.array(zDriverLicense).min(1, { message: 'Хотя бы одни водительские права обязательны' }),
    pts: zPts.nullable().optional(),
    epts: zEpts.nullable().optional(),
    sts: zSts.nullable().optional(),
    applicationIssueDate: zDateString,
    isTrailerUsed: z.boolean(),
    vehicleUsagePurpose: zVehicleUsagePurpose,
    insuranceAccessType: z.enum(['limited', 'unlimited']),
  })
  .merge(zPersonalKaskoGeneralFields)
export type PersonalKaskoFromScratchInput = z.output<typeof zPersonalKaskoFromScratchInput>

export const zPersonalKaskoByOsagoInput = z
  .object({
    policyType: z.literal('byOsago'),
    insuranceScenariosKeys: z.array(zPersonalKaskoByOsagoInsuranceScenarioKey),
    policyholderPhone: zPhoneRequired,
    policyholderEmail: zEmailRequired,
    policyholderLastName: zStringRequired,
    policyholderFirstName: zStringRequired,
    policyholderMiddleName: zStringRequired,
    policyholderResidenceRegion: zStringOptionalNullable,
    policyholderPassportIssuedBy: zStringOptionalNullable,
    carOwnerResidenceRegion: zStringOptionalNullable,
    vehicleDocPowerHp: zStringOptionalNullable,
    vehicleVin: zStringOptionalNullable,
    extraDocType: z
      .union([z.enum(['ПТС', 'ЭПТС']), z.literal('')])
      .nullable()
      .optional(),
    extraDocSeries: zStringOptionalNullable,
    extraDocNumber: zStringOptionalNullable,
    extraDocIssueDate: zDateStringOrEmpty,
    alfaOsagoExternalId: z.string().nullable().optional(),
    ingosOsagoExternalId: z.string().nullable().optional(),
    renesOsagoExternalId: z.string().nullable().optional(),
    rosgosOsagoExternalId: z.string().nullable().optional(),
    vskOsagoExternalId: z.string().nullable().optional(),
  })
  .merge(zPersonalKaskoGeneralFields)
export type PersonalKaskoByOsagoInput = z.output<typeof zPersonalKaskoByOsagoInput>
export type PersonalKaskoByOsagoInputFieldName = keyof PersonalKaskoByOsagoInput

export const kaskoAlfaRequiredGeneralFieldNames = [
  'lastOwnershipStartDate',
] satisfies PersonalKaskoByOsagoInputFieldName[]
export const kaskoIngosRequiredGeneralFieldNames = [
  'lastOwnershipStartDate',
  'mileage',
  'vehicleCost',
  'steeringWheelLocation',
  'transmissionType',
  'vehicleColor',
  'vehicleUsagePurpose',
] satisfies PersonalKaskoByOsagoInputFieldName[]
export const kaskoRenesRequiredGeneralFieldNames = [
  'policyholderResidenceRegion',
  'carOwnerResidenceRegion',
  'vehicleDocPowerHp',
  'transmissionType',
  'children',
  'maritalStatus',
] satisfies PersonalKaskoByOsagoInputFieldName[]
export const kaskoRosgosRequiredGeneralFieldNames = [
  'mileage',
  'vehicleCost',
] satisfies PersonalKaskoByOsagoInputFieldName[]
export const kaskoVskRequiredGeneralFieldNames = [
  'lastOwnershipStartDate',
  'vehicleCost',
  'maritalStatus',
  'extraDocType',
  'extraDocSeries',
  'extraDocNumber',
  'extraDocIssueDate',
  'policyholderPassportIssuedBy',
  'vehicleVin',
] satisfies PersonalKaskoByOsagoInputFieldName[]

export const getKaskoRequiredGeneralFieldNames = ({
  insuranceScenariosKeys,
}: {
  insuranceScenariosKeys: InsuranceScenarioKey[]
}) => {
  const requiredFieldsNames: PersonalKaskoByOsagoInputFieldName[] = []
  if (insuranceScenariosKeys.includes('personalKaskoByOsagoAlfa')) {
    requiredFieldsNames.push(...kaskoAlfaRequiredGeneralFieldNames)
  }
  if (insuranceScenariosKeys.includes('personalKaskoByOsagoIngos')) {
    requiredFieldsNames.push(...kaskoIngosRequiredGeneralFieldNames)
  }
  if (insuranceScenariosKeys.includes('personalKaskoByOsagoRenes')) {
    requiredFieldsNames.push(...kaskoRenesRequiredGeneralFieldNames)
  }
  if (insuranceScenariosKeys.includes('personalKaskoByOsagoRosgos')) {
    requiredFieldsNames.push(...kaskoRosgosRequiredGeneralFieldNames)
  }
  if (insuranceScenariosKeys.includes('personalKaskoByOsagoVsk')) {
    requiredFieldsNames.push(...kaskoVskRequiredGeneralFieldNames)
  }
  return [...new Set(requiredFieldsNames)] as PersonalKaskoByOsagoInputFieldName[]
}

export const zPersonalKaskoMashedInput = zPersonalKaskoFromScratchInput
  .omit({
    policyType: true,
    insuranceScenariosKeys: true,
  })
  .extend({
    policyType: z.literal('mashed'),
    insuranceScenariosKeys: z.array(zPersonalKaskoInsuranceScenarioKey),
  })
  .merge(zPersonalKaskoGeneralFields)
export type PersonalKaskoMashedInput = z.output<typeof zPersonalKaskoMashedInput>

export const zPersonalKaskoInput = z
  .discriminatedUnion('policyType', [
    zPersonalKaskoFromScratchInput,
    zPersonalKaskoByOsagoInput,
    zPersonalKaskoMashedInput,
  ])
  .superRefine((data, ctx) => {
    const insuranceScenariosKeysFromScratch = filterPersonalKaskoFromScratchInsuranceScenarioKeys({
      insuranceScenariosKeys: data.insuranceScenariosKeys,
    })
    if (insuranceScenariosKeysFromScratch.length > 0) {
      if (!('stsOrPtsOrEpts' in data) || (data.stsOrPtsOrEpts === 'pts' && !data.pts)) {
        ctx.addIssue({
          path: ['pts'],
          code: z.ZodIssueCode.custom,
          message: 'ПТС обязателен',
        })
      }
      if (!('stsOrPtsOrEpts' in data) || (data.stsOrPtsOrEpts === 'sts' && !data.sts)) {
        ctx.addIssue({
          path: ['sts'],
          code: z.ZodIssueCode.custom,
          message: 'СТС обязателен',
        })
      }
      if (!('stsOrPtsOrEpts' in data) || (data.stsOrPtsOrEpts === 'epts' && !data.epts)) {
        ctx.addIssue({
          path: ['epts'],
          code: z.ZodIssueCode.custom,
          message: 'Электронный ПТС обязателен',
        })
      }
      if (!('policyholderIsCarOwner' in data) || (!data.policyholderIsCarOwner && !data.carOwnerPassport)) {
        ctx.addIssue({
          path: ['carOwnerPassport'],
          code: z.ZodIssueCode.custom,
          message: 'Пасспорт собственника автомобиля обязателен',
        })
      }
    }
    const insuranceScenariosKeysByOsago = filterPersonalKaskoByOsagoInsuranceScenarioKeys({
      insuranceScenariosKeys: data.insuranceScenariosKeys,
    })
    if (insuranceScenariosKeysByOsago.includes('personalKaskoByOsagoAlfa')) {
      if (!('alfaOsagoExternalId' in data) || !data.alfaOsagoExternalId?.trim()) {
        ctx.addIssue({
          path: ['alfaOsagoExternalId'],
          code: z.ZodIssueCode.custom,
          message: `Номер страховки ОСАГО обязателен`,
        })
      }
    }
    if (insuranceScenariosKeysByOsago.includes('personalKaskoByOsagoIngos')) {
      if (!('ingosOsagoExternalId' in data) || !data.ingosOsagoExternalId?.trim()) {
        ctx.addIssue({
          path: ['ingosOsagoExternalId'],
          code: z.ZodIssueCode.custom,
          message: `Номер страховки ОСАГО обязателен`,
        })
      }
    }
    if (insuranceScenariosKeysByOsago.includes('personalKaskoByOsagoRenes')) {
      if (!('renesOsagoExternalId' in data) || !data.renesOsagoExternalId?.trim()) {
        ctx.addIssue({
          path: ['renesOsagoExternalId'],
          code: z.ZodIssueCode.custom,
          message: `Номер страховки ОСАГО обязателен`,
        })
      }
    }
    if (insuranceScenariosKeysByOsago.includes('personalKaskoByOsagoRosgos')) {
      if (!('rosgosOsagoExternalId' in data) || !data.rosgosOsagoExternalId?.trim()) {
        ctx.addIssue({
          path: ['rosgosOsagoExternalId'],
          code: z.ZodIssueCode.custom,
          message: `Номер страховки ОСАГО обязателен`,
        })
      }
    }
    if (insuranceScenariosKeysByOsago.includes('personalKaskoByOsagoVsk')) {
      if (!('vskOsagoExternalId' in data) || !data.vskOsagoExternalId?.trim()) {
        ctx.addIssue({
          path: ['vskOsagoExternalId'],
          code: z.ZodIssueCode.custom,
          message: `Номер страховки ОСАГО обязателен`,
        })
      }
    }
    const requiredKaskoGeneralFieldsNames = getKaskoRequiredGeneralFieldNames({
      insuranceScenariosKeys: data.insuranceScenariosKeys,
    })
    for (const field of requiredKaskoGeneralFieldsNames) {
      if (field === 'extraDocSeries' && 'extraDocType' in data && data.extraDocType === 'ЭПТС') {
        continue
      }
      if (!(data as any)[field]?.trim()) {
        ctx.addIssue({
          path: [field],
          code: z.ZodIssueCode.custom,
          message: `Обязательное поле`,
        })
      }
    }
  })

export type PersonalKaskoInput = z.output<typeof zPersonalKaskoInput>
type MergeUnion<U> = {
  [K in U extends any ? keyof U : never]: U extends Partial<Record<K, infer T>> ? T : never
}
export type PersonalKaskoInputMerged = MergeUnion<PersonalKaskoInput>
export const personalKaskoInputExampleValues: PersonalKaskoInputMerged = {
  policyType: 'mashed',
  insuranceScenariosKeys: [
    'personalKaskoFromScratchAlfa',
    'personalKaskoFromScratchIngos',
    'personalKaskoFromScratchRenes',
    'personalKaskoFromScratchRosgos',
    'personalKaskoFromScratchVsk',
  ],
  stsOrPtsOrEpts: 'sts',
  policyholderLastName: 'Иванов',
  policyholderFirstName: 'Иван',
  policyholderMiddleName: 'Иванович',
  policyholderResidenceRegion: 'Москва',
  carOwnerResidenceRegion: 'Москва',
  vehicleDocPowerHp: '100',
  policyholderPhone: '+79090099900',
  policyholderEmail: 'test@test.com',
  policyholderIsCarOwner: true,
  policyholderPassport: passportExampleValues,
  carOwnerPassport: passportExampleValues,
  driversLicenses: [driverLicenseExampleValues],
  sts: stsExampleValues,
  pts: ptsExampleValues,
  epts: eptsExampleValues,
  applicationIssueDate: '01.01.2025',
  insuranceStartDate: '01.01.2025',
  isTrailerUsed: false,
  vehicleUsagePurpose: 'personal',
  insuranceAccessType: 'limited',
  alfaOsagoExternalId: '61283566',
  ingosOsagoExternalId: 'https://ingogate.ingos.ru/PolicyOsago/Browser?policyIsn=3279047410803',
  renesOsagoExternalId: 'https://prod.renins.com/SFA-CORE2/Policy/Edit/SGQ-090725-368',
  rosgosOsagoExternalId: '',
  vskOsagoExternalId:
    'https://lka.vsk.ru/products/forge/osago?step=checkout&documentId=8337deb1-5ecb-4d22-b787-dcc2d44db99d',
  lastOwnershipStartDate: '01.01.2024',
  mileage: '100000',
  vehicleCost: '1000000',
  steeringWheelLocation: 'Левый',
  transmissionType: 'Механическая',
  vehicleColor: 'Красный',
  children: 'Дети есть',
  maritalStatus: 'Холост',
  extraDocType: 'ЭПТС',
  extraDocSeries: '',
  extraDocNumber: '164301000121588',
  extraDocIssueDate: '01.01.2025',
  policyholderPassportIssuedBy: 'ГУ МВД России по Москве',
  vehicleVin: 'XWEPC811DD0040464',
}

export const personalKaskoGeneral = {
  title: 'КАСКО физ. лица',
  insuranceType: 'personalKasko',
  insuranceCustomerType: 'personal',
  zInput: zPersonalKaskoInput,
  exampleValues: personalKaskoInputExampleValues,
  insuranceScenariosKeys: [
    'personalKaskoFromScratchAlfa',
    'personalKaskoFromScratchIngos',
    'personalKaskoFromScratchRenes',
    'personalKaskoFromScratchRosgos',
    'personalKaskoFromScratchVsk',
    'personalKaskoByOsagoAlfa',
    'personalKaskoByOsagoIngos',
    'personalKaskoByOsagoRenes',
    'personalKaskoByOsagoRosgos',
    'personalKaskoByOsagoVsk',
  ],
} satisfies InsuranceTypeGeneral
